import { useState, useEffect, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckSquare, MessageSquare, Trash2, X,
  Calendar, Flag, UserPlus, Clock,
  ChevronRight, Archive, AlignLeft, Plus,
  Paperclip, FileText, Image, File, Download, MoreVertical
} from 'lucide-react'
import { toast } from 'sonner'
import { cardsApi, checklistsApi, checklistItemsApi, listsApi, boardsApi, attachmentsApi, customFieldsApi, auditLogsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  BoardCard, BoardLabel, CardPriority, BoardList,
  CreateBoardCardRequest, CardComment, CardChecklist, RecurrenceFrequency,
  CardAttachment, FieldConfiguration, CardDependency, AuditLog
} from '@/types'
import { Repeat, Link2, History } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDistanceToNow } from 'date-fns'
import { ScrollArea } from '@/components/ui/scroll-area'

interface TaskModalProps {
  card?: BoardCard
  listId?: string
  lists?: BoardList[]
  boardId: string
  boardName?: string
  projectName?: string
  labels: BoardLabel[]
  onClose: () => void
  mode: 'create' | 'edit'
}

const PRIORITY_OPTIONS: { value: CardPriority; label: string; color: string }[] = [
  { value: 'LOW', label: 'Low', color: 'bg-gray-500' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-blue-500' },
  { value: 'HIGH', label: 'High', color: 'bg-orange-500' },
  { value: 'URGENT', label: 'Urgent', color: 'bg-red-500' },
]

// Types for pending items in create mode
interface PendingChecklist {
  id: string
  name: string
  items: { id: string; text: string }[]
}

export default function TaskModal({
  card, listId: initialListId, lists, boardId, projectName, labels: _labels, onClose, mode
}: TaskModalProps) {
  // _labels is available for future use (e.g., label management)
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(card?.card_title || '')
  const [description, setDescription] = useState(card?.card_description || '')
  const [dueDate, setDueDate] = useState(card?.due_date ? card.due_date.split('T')[0] : '')
  const [priority, setPriority] = useState<CardPriority | ''>(card?.priority || '')
  const [estimatedHours, setEstimatedHours] = useState<string>(card?.estimated_hours?.toString() || '')
  // Auto-select first list if no initialListId provided
  const selectedListId = useMemo(() => {
    return initialListId || lists?.[0]?.list_id || ''
  }, [initialListId, lists])
  const [newChecklistName, setNewChecklistName] = useState('')
  const [addingChecklist, setAddingChecklist] = useState(false)
  const [newItemTexts, setNewItemTexts] = useState<Record<string, string>>({})
  const [newComment, setNewComment] = useState('')
  const [localComments, setLocalComments] = useState<CardComment[]>([])
  const [localChecklists, setLocalChecklists] = useState<CardChecklist[]>(card?.checklists || [])
  const [localMembers, setLocalMembers] = useState(card?.members || [])

  // Pending items for create mode (will be created after card is created)
  const [pendingChecklists, setPendingChecklists] = useState<PendingChecklist[]>([])
  const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false)

  // Recurring task state
  const [isRecurring, setIsRecurring] = useState(card?.is_recurring || false)
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceFrequency | ''>(card?.recurrence_frequency || '')
  const [recurrenceStartDate, setRecurrenceStartDate] = useState(card?.recurrence_start_date ? card.recurrence_start_date.split('T')[0] : '')
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(card?.recurrence_end_date ? card.recurrence_end_date.split('T')[0] : '')

  // Attachments state
  const [localAttachments, setLocalAttachments] = useState<CardAttachment[]>(card?.attachments || [])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Dependencies state
  const [localDependencies, setLocalDependencies] = useState<CardDependency[]>(card?.dependencies || [])
  const [dependencyPopoverOpen, setDependencyPopoverOpen] = useState(false)

  // Custom fields state
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(() => {
    const values: Record<string, string> = {}
    if (card?.custom_fields) {
      Object.entries(card.custom_fields).forEach(([configId, field]) => {
        values[configId] = field.field_value || ''
      })
    }
    return values
  })

  // Fetch board members for assignee selection (only users with access to this board)
  const { data: boardMembers = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ['board-members', boardId],
    queryFn: () => boardsApi.getMembers(boardId),
    enabled: !!boardId,
  })

  // Filter out users who are already assigned to this card
  const assignedUserIds = new Set(localMembers?.map(m => m.user_id) || [])
  const availableUsers = boardMembers.filter(m => !assignedUserIds.has(m.user_id))

  const { data: fetchedComments } = useQuery({
    queryKey: ['card-comments', card?.card_id],
    queryFn: () => card ? cardsApi.getComments(card.card_id) : Promise.resolve([]),
    enabled: mode === 'edit' && !!card?.card_id,
  })

  // Fetch attachments
  const { data: fetchedAttachments } = useQuery({
    queryKey: ['card-attachments', card?.card_id],
    queryFn: () => card ? attachmentsApi.list(card.card_id) : Promise.resolve([]),
    enabled: mode === 'edit' && !!card?.card_id,
  })

  // Fetch custom field configurations for CARD entity type
  const { data: cardFieldConfigs = [] } = useQuery({
    queryKey: ['field-configs', 'CARD'],
    queryFn: () => customFieldsApi.getAll('CARD'),
  })

  // Fetch board data to get all cards for dependency selection
  const { data: boardData } = useQuery({
    queryKey: ['board', boardId],
    queryFn: () => boardsApi.getById(boardId),
    enabled: mode === 'edit' && !!boardId,
  })

  // Get all cards from the board except the current card
  const allBoardCards = useMemo(() => {
    if (!boardData?.lists) return []
    const cards: BoardCard[] = []
    boardData.lists.forEach(list => {
      list.cards?.forEach(c => {
        if (c.card_id !== card?.card_id) {
          cards.push(c)
        }
      })
    })
    return cards
  }, [boardData, card?.card_id])

  // Filter out cards that are already dependencies
  const dependencyCardIds = new Set(localDependencies.map(d => d.depends_on_card_id))
  const availableCardsForDependency = allBoardCards.filter(c => !dependencyCardIds.has(c.card_id))

  // Fetch audit logs for activity history
  const { data: activityLogs = [], refetch: refetchActivity } = useQuery({
    queryKey: ['card-activity', card?.card_id],
    queryFn: async () => {
      console.log('Fetching activity for card:', card?.card_id)
      if (!card) return []
      const logs = await auditLogsApi.getByEntity('CARD', card.card_id)
      console.log('Activity logs result:', logs)
      return logs
    },
    enabled: mode === 'edit' && !!card?.card_id,
    staleTime: 0,
    gcTime: 0,
  })

  // Force refetch when modal opens in edit mode
  useEffect(() => {
    if (mode === 'edit' && card?.card_id) {
      console.log('Forcing activity refetch')
      refetchActivity()
    }
  }, [mode, card?.card_id, refetchActivity])

  useEffect(() => {
    if (fetchedComments) setLocalComments(fetchedComments)
  }, [fetchedComments])

  useEffect(() => {
    if (fetchedAttachments) setLocalAttachments(fetchedAttachments)
  }, [fetchedAttachments])

  // Update custom field values when card data changes (e.g., after reopening modal)
  useEffect(() => {
    if (card?.custom_fields) {
      const values: Record<string, string> = {}
      Object.entries(card.custom_fields).forEach(([configId, field]) => {
        values[configId] = field.field_value || ''
      })
      setCustomFieldValues(values)
    }
  }, [card?.custom_fields])

  const createCardMutation = useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: CreateBoardCardRequest }) =>
      listsApi.createCard(listId, data),
    onSuccess: async (newCard) => {
      // Create pending checklists
      for (const checklist of pendingChecklists) {
        try {
          const createdChecklist = await cardsApi.createChecklist(newCard.card_id, { checklist_name: checklist.name })
          // Create checklist items
          for (const item of checklist.items) {
            await checklistsApi.createItem(createdChecklist.checklist_id, { item_text: item.text })
          }
        } catch {
          console.error('Failed to create checklist')
        }
      }

      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Task created')
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create task'
      toast.error(errorMessage)
    },
  })

  const updateCardMutation = useMutation({
    mutationFn: (data: Partial<BoardCard> | { custom_fields: Record<string, string> }) => cardsApi.update(card!.card_id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      queryClient.invalidateQueries({ queryKey: ['card-activity', card?.card_id] })
      toast.success('Task updated')
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update task'
      toast.error(errorMessage)
    },
  })

  // Mutation for inline updates (doesn't close modal)
  const updateCardInlineMutation = useMutation({
    mutationFn: (data: Partial<BoardCard>) => cardsApi.update(card!.card_id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Updated')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update'
      toast.error(errorMessage)
    },
  })

  const archiveCardMutation = useMutation({
    mutationFn: () => cardsApi.archive(card!.card_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      queryClient.invalidateQueries({ queryKey: ['archivedCards', boardId] })
      toast.success('Task archived')
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive task'
      toast.error(errorMessage)
    },
  })

  const createChecklistMutation = useMutation({
    mutationFn: (name: string) => cardsApi.createChecklist(card!.card_id, { checklist_name: name }),
    onSuccess: (newChecklist) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      // Add to local state immediately
      setLocalChecklists(prev => [...prev, { ...newChecklist, items: [] }])
      setNewChecklistName('')
      setAddingChecklist(false)
      toast.success('Checklist added')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add checklist'
      toast.error(errorMessage)
    },
  })

  const deleteChecklistMutation = useMutation({
    mutationFn: (checklistId: string) => checklistsApi.delete(checklistId),
    onSuccess: (_, checklistId) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      // Remove from local state immediately
      setLocalChecklists(prev => prev.filter(c => c.checklist_id !== checklistId))
      toast.success('Checklist deleted')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete checklist'
      toast.error(errorMessage)
    },
  })

  const createChecklistItemMutation = useMutation({
    mutationFn: ({ checklistId, itemText }: { checklistId: string; itemText: string }) =>
      checklistsApi.createItem(checklistId, { item_text: itemText }),
    onSuccess: (newItem, variables) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      // Add item to local checklist state immediately
      setLocalChecklists(prev => prev.map(c =>
        c.checklist_id === variables.checklistId
          ? { ...c, items: [...(c.items || []), newItem] }
          : c
      ))
      setNewItemTexts(prev => ({ ...prev, [variables.checklistId]: '' }))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add item'
      toast.error(errorMessage)
    },
  })

  const updateChecklistItemMutation = useMutation({
    mutationFn: ({ itemId, isCompleted }: { itemId: string; isCompleted: boolean }) =>
      checklistItemsApi.update(itemId, { is_completed: isCompleted }),
    onSuccess: (_updatedItem, variables) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      // Update item in local state immediately
      setLocalChecklists(prev => prev.map(c => ({
        ...c,
        items: c.items?.map(item =>
          item.item_id === variables.itemId
            ? { ...item, is_completed: variables.isCompleted }
            : item
        ) || []
      })))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update item'
      toast.error(errorMessage)
    },
  })

  const deleteChecklistItemMutation = useMutation({
    mutationFn: (itemId: string) => checklistItemsApi.delete(itemId),
    onSuccess: (_, itemId) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      // Remove item from local state immediately
      setLocalChecklists(prev => prev.map(c => ({
        ...c,
        items: c.items?.filter(item => item.item_id !== itemId) || []
      })))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete item'
      toast.error(errorMessage)
    },
  })

  const createCommentMutation = useMutation({
    mutationFn: (content: string) => cardsApi.createComment(card!.card_id, { comment_text: content }),
    onSuccess: (newCommentData) => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      queryClient.invalidateQueries({ queryKey: ['card-comments', card?.card_id] })
      setLocalComments(prev => [newCommentData, ...prev])
      setNewComment('')
      toast.success('Comment added')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add comment'
      toast.error(errorMessage)
    },
  })

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => cardsApi.addMember(card!.card_id, userId),
    onSuccess: (_, userId) => {
      // Find the user from boardMembers and add to local state
      const member = boardMembers.find(m => m.user_id === userId)
      if (member) {
        setLocalMembers(prev => [...prev, {
          card_id: card!.card_id,
          user_id: member.user_id,
          full_name: member.full_name,
          email: member.user_email,
          created_at: new Date().toISOString(),
        }])
      }
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      setAssigneePopoverOpen(false)
      toast.success('Assignee added')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add assignee'
      toast.error(errorMessage)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => cardsApi.removeMember(card!.card_id, userId),
    onSuccess: (_, userId) => {
      setLocalMembers(prev => prev.filter(m => m.user_id !== userId))
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Assignee removed')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove assignee'
      toast.error(errorMessage)
    },
  })

  // Attachment mutations
  const uploadAttachmentMutation = useMutation({
    mutationFn: (file: File) => attachmentsApi.upload(card!.card_id, file),
    onSuccess: (newAttachment) => {
      setLocalAttachments(prev => [newAttachment, ...prev])
      queryClient.invalidateQueries({ queryKey: ['card-attachments', card?.card_id] })
      toast.success('File uploaded successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload file'
      toast.error(errorMessage)
    },
  })

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.delete(card!.card_id, attachmentId),
    onSuccess: (_, attachmentId) => {
      setLocalAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId))
      queryClient.invalidateQueries({ queryKey: ['card-attachments', card?.card_id] })
      toast.success('Attachment deleted')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete attachment'
      toast.error(errorMessage)
    },
  })

  const deleteCardMutation = useMutation({
    mutationFn: () => cardsApi.delete(card!.card_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Task deleted')
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete task'
      toast.error(errorMessage)
    },
  })

  const addDependencyMutation = useMutation({
    mutationFn: (dependsOnCardId: string) => cardsApi.addDependency(card!.card_id, { depends_on_card_id: dependsOnCardId }),
    onSuccess: (newDependency) => {
      // Find the card title from allBoardCards
      const dependsOnCard = allBoardCards.find(c => c.card_id === newDependency.depends_on_card_id)
      const enrichedDependency = {
        ...newDependency,
        depends_on_card_title: dependsOnCard?.card_title || ''
      }
      setLocalDependencies(prev => [...prev, enrichedDependency])
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Dependency added')
      setDependencyPopoverOpen(false)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add dependency'
      toast.error(errorMessage)
    },
  })

  const removeDependencyMutation = useMutation({
    mutationFn: (dependencyId: string) => cardsApi.removeDependency(dependencyId),
    onSuccess: (_, dependencyId) => {
      setLocalDependencies(prev => prev.filter(d => d.dependency_id !== dependencyId))
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Dependency removed')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove dependency'
      toast.error(errorMessage)
    },
  })

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadAttachmentMutation.mutate(file)
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith('image/')) return <Image className="h-4 w-4" />
    if (fileType?.includes('pdf')) return <FileText className="h-4 w-4" />
    return <File className="h-4 w-4" />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getActivityDescription = (log: AuditLog): string => {
    switch (log.action) {
      case 'CREATE':
        return 'created this card'
      case 'UPDATE':
        if (log.description?.includes('Moved card')) return 'moved this card'
        if (log.description?.includes('Added label')) return 'added a label'
        if (log.description?.includes('Removed label')) return 'removed a label'
        if (log.description?.includes('Added member')) return 'added a member'
        if (log.description?.includes('Removed member')) return 'removed a member'
        return 'updated this card'
      case 'DELETE':
        return 'deleted this card'
      case 'ARCHIVE':
        return 'archived this card'
      case 'RESTORE':
        return 'restored this card'
      default:
        return log.action.toLowerCase()
    }
  }

  const handleCreate = () => {
    if (!title.trim() || !selectedListId) return
    const data: CreateBoardCardRequest = {
      card_title: title,
      card_description: description || undefined,
      due_date: dueDate ? new Date(dueDate + 'T00:00:00Z').toISOString() : undefined,
      priority: priority || undefined,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
      is_recurring: isRecurring || undefined,
      recurrence_frequency: recurrenceFrequency || undefined,
      recurrence_start_date: recurrenceStartDate ? new Date(recurrenceStartDate + 'T00:00:00Z').toISOString() : undefined,
      recurrence_end_date: recurrenceEndDate ? new Date(recurrenceEndDate + 'T00:00:00Z').toISOString() : undefined,
    }
    createCardMutation.mutate({ listId: selectedListId, data })
  }

  const handleSave = () => {
    if (mode === 'create') return
    const updates: any = {}
    if (title !== card?.card_title) updates.card_title = title
    if (description !== (card?.card_description || '')) updates.card_description = description || null
    if (dueDate !== (card?.due_date?.split('T')[0] || '')) {
      updates.due_date = dueDate ? new Date(dueDate + 'T00:00:00Z').toISOString() : null
    }
    if (priority !== (card?.priority || '')) updates.priority = priority || null
    const currentEstimatedHours = card?.estimated_hours?.toString() || ''
    if (estimatedHours !== currentEstimatedHours) {
      updates.estimated_hours = estimatedHours ? parseFloat(estimatedHours) : null
    }
    // Recurring task fields
    if (isRecurring !== (card?.is_recurring || false)) updates.is_recurring = isRecurring
    if (recurrenceFrequency !== (card?.recurrence_frequency || '')) updates.recurrence_frequency = recurrenceFrequency || null
    if (recurrenceStartDate !== (card?.recurrence_start_date?.split('T')[0] || '')) {
      updates.recurrence_start_date = recurrenceStartDate ? new Date(recurrenceStartDate + 'T00:00:00Z').toISOString() : null
    }
    if (recurrenceEndDate !== (card?.recurrence_end_date?.split('T')[0] || '')) {
      updates.recurrence_end_date = recurrenceEndDate ? new Date(recurrenceEndDate + 'T00:00:00Z').toISOString() : null
    }

    // Custom fields - check if any values have changed
    const hasCustomFieldChanges = Object.keys(customFieldValues).some(configId => {
      const currentValue = customFieldValues[configId] || ''
      const originalValue = card?.custom_fields?.[configId]?.field_value || ''
      return currentValue !== originalValue
    })
    if (hasCustomFieldChanges) {
      updates.custom_fields = customFieldValues
    }

    if (Object.keys(updates).length > 0) {
      updateCardMutation.mutate(updates)
    } else {
      // No changes, just close the modal
      onClose()
    }
  }

  const handleAddChecklist = () => {
    if (!newChecklistName.trim()) return
    if (mode === 'create') {
      // Add to pending checklists for create mode
      const newChecklist: PendingChecklist = {
        id: `pending-${Date.now()}`,
        name: newChecklistName,
        items: []
      }
      setPendingChecklists(prev => [...prev, newChecklist])
      setNewChecklistName('')
      setAddingChecklist(false)
    } else {
      createChecklistMutation.mutate(newChecklistName)
    }
  }

  const handleAddChecklistItem = (checklistId: string) => {
    const itemText = newItemTexts[checklistId]
    if (!itemText?.trim()) return
    if (mode === 'create') {
      // Add to pending checklist items
      setPendingChecklists(prev => prev.map(c =>
        c.id === checklistId
          ? { ...c, items: [...c.items, { id: `item-${Date.now()}`, text: itemText }] }
          : c
      ))
      setNewItemTexts(prev => ({ ...prev, [checklistId]: '' }))
    } else {
      createChecklistItemMutation.mutate({ checklistId, itemText })
    }
  }

  const handleDeletePendingChecklist = (checklistId: string) => {
    setPendingChecklists(prev => prev.filter(c => c.id !== checklistId))
  }

  const handleDeletePendingChecklistItem = (checklistId: string, itemId: string) => {
    setPendingChecklists(prev => prev.map(c =>
      c.id === checklistId
        ? { ...c, items: c.items.filter(i => i.id !== itemId) }
        : c
    ))
  }

  const handleAddComment = () => {
    if (!newComment.trim()) return
    createCommentMutation.mutate(newComment)
  }

  // State for popover controls
  const [priorityPopoverOpen, setPriorityPopoverOpen] = useState(false)
  const [dueDatePopoverOpen, setDueDatePopoverOpen] = useState(false)
  const [effortPopoverOpen, setEffortPopoverOpen] = useState(false)
  const [recurringPopoverOpen, setRecurringPopoverOpen] = useState(false)

  // Calculate checklist progress
  const getChecklistProgress = () => {
    const allChecklists = mode === 'create' ? pendingChecklists : localChecklists
    let completed = 0
    let total = 0
    allChecklists.forEach(checklist => {
      const items = mode === 'create'
        ? (checklist as PendingChecklist).items
        : (checklist as CardChecklist).items || []
      total += items.length
      if (mode === 'edit') {
        completed += (items as any[]).filter(i => i.is_completed).length
      }
    })
    return { completed, total }
  }

  const checklistProgress = getChecklistProgress()

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden bg-slate-50 dark:bg-slate-900 [&>button]:hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>
            {mode === 'create' ? 'Create Task' : 'Edit Task'}
          </DialogTitle>
        </DialogHeader>

        {/* Header with breadcrumb and actions */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-white dark:bg-slate-800">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {projectName ? (
              <>
                <span className="font-medium text-foreground">{projectName}</span>
                <ChevronRight className="h-3 w-3" />
                <span className="text-muted-foreground">
                  {lists?.find(l => l.list_id === selectedListId)?.list_name || 'Swimlane'}
                </span>
              </>
            ) : (
              <span className="font-medium text-foreground">Task Details</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {mode === 'edit' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                        deleteCardMutation.mutate()
                      }
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Main content area - two column layout */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left column - Main content */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-white dark:bg-slate-800">
            <ScrollArea className="flex-1">
              <div className="p-6 space-y-6">
                {/* Title */}
                <Input
                  id="task-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={mode === 'edit' ? handleSave : undefined}
                  placeholder="Enter task title..."
                  className="text-2xl font-bold border-0 px-0 focus-visible:ring-0 shadow-none bg-transparent h-auto py-0"
                  autoFocus={mode === 'create'}
                />

                {/* Add & Assignee Buttons */}
                <div className="flex items-center gap-2 flex-wrap relative">
                  {/* Add Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Add
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem onClick={() => setDueDatePopoverOpen(true)}>
                        <Calendar className="mr-2 h-4 w-4" />
                        Date
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEffortPopoverOpen(true)}>
                        <Clock className="mr-2 h-4 w-4" />
                        Effort
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPriorityPopoverOpen(true)}>
                        <Flag className="mr-2 h-4 w-4" />
                        Priority
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setRecurringPopoverOpen(true)}>
                        <Repeat className="mr-2 h-4 w-4" />
                        Recurring
                      </DropdownMenuItem>
                      {mode === 'edit' && (
                        <DropdownMenuItem onClick={() => setDependencyPopoverOpen(true)}>
                          <Link2 className="mr-2 h-4 w-4" />
                          Dependency
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Assignee Button */}
                  {mode === 'edit' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssigneePopoverOpen(!assigneePopoverOpen)}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Assignee
                    </Button>
                  )}

                  {/* Assignee Popover */}
                  {assigneePopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setAssigneePopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[250px] p-2 bg-popover border rounded-md shadow-md">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium px-2">Add Assignee</Label>
                          {isLoadingMembers ? (
                            <div className="px-2 py-4 text-sm text-muted-foreground">Loading...</div>
                          ) : availableUsers.length === 0 ? (
                            <div className="px-2 py-4 text-sm text-muted-foreground">No available users</div>
                          ) : (
                            <div className="max-h-[200px] overflow-y-auto">
                              {availableUsers.map((member) => (
                                <button
                                  key={member.user_id}
                                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted"
                                  onClick={() => addMemberMutation.mutate(member.user_id)}
                                >
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="text-left">
                                    <div className="font-medium">{member.full_name}</div>
                                    <div className="text-xs text-muted-foreground">{member.user_email}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Due Date Inline Form */}
                  {dueDatePopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setDueDatePopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[280px] p-4 bg-popover border rounded-md shadow-md">
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Due Date</Label>
                          <Input
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="w-full"
                            autoFocus
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDueDatePopoverOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setDueDatePopoverOpen(false)
                                if (mode === 'edit') {
                                  updateCardInlineMutation.mutate({
                                    due_date: dueDate ? new Date(dueDate + 'T00:00:00Z').toISOString() : null
                                  } as Partial<BoardCard>)
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Effort Inline Form */}
                  {effortPopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setEffortPopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[280px] p-4 bg-popover border rounded-md shadow-md">
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Estimated Effort (hours)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={estimatedHours}
                            onChange={(e) => setEstimatedHours(e.target.value)}
                            placeholder="e.g., 4"
                            className="w-full"
                            autoFocus
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEffortPopoverOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setEffortPopoverOpen(false)
                                if (mode === 'edit') {
                                  updateCardInlineMutation.mutate({
                                    estimated_hours: estimatedHours ? parseFloat(estimatedHours) : null
                                  } as Partial<BoardCard>)
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Priority Inline Form */}
                  {priorityPopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setPriorityPopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[200px] p-2 bg-popover border rounded-md shadow-md">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium px-2">Priority</Label>
                          {PRIORITY_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted"
                              onClick={() => {
                                setPriority(opt.value)
                                setPriorityPopoverOpen(false)
                                if (mode === 'edit') {
                                  updateCardInlineMutation.mutate({
                                    priority: opt.value || null
                                  } as Partial<BoardCard>)
                                }
                              }}
                            >
                              <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Recurring Inline Form */}
                  {recurringPopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setRecurringPopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[300px] p-4 bg-popover border rounded-md shadow-md">
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Recurring Task</Label>
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Frequency</Label>
                            <Select
                              value={recurrenceFrequency}
                              onValueChange={(value: RecurrenceFrequency) => {
                                setRecurrenceFrequency(value)
                                if (!isRecurring) setIsRecurring(true)
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="DAILY">Daily</SelectItem>
                                <SelectItem value="WEEKLY">Weekly</SelectItem>
                                <SelectItem value="BIWEEKLY">Bi-weekly</SelectItem>
                                <SelectItem value="MONTHLY">Monthly</SelectItem>
                                <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                                <SelectItem value="YEARLY">Yearly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Start Date</Label>
                            <Input
                              type="date"
                              value={recurrenceStartDate}
                              onChange={(e) => setRecurrenceStartDate(e.target.value)}
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">End Date (Optional)</Label>
                            <Input
                              type="date"
                              value={recurrenceEndDate}
                              onChange={(e) => setRecurrenceEndDate(e.target.value)}
                              className="w-full"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRecurringPopoverOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setRecurringPopoverOpen(false)
                                if (mode === 'edit') {
                                  updateCardInlineMutation.mutate({
                                    is_recurring: isRecurring,
                                    recurrence_frequency: recurrenceFrequency || null,
                                    recurrence_start_date: recurrenceStartDate ? new Date(recurrenceStartDate + 'T00:00:00Z').toISOString() : null,
                                    recurrence_end_date: recurrenceEndDate ? new Date(recurrenceEndDate + 'T00:00:00Z').toISOString() : null
                                  } as Partial<BoardCard>)
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Dependency Inline Form */}
                  {dependencyPopoverOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setDependencyPopoverOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-50 w-[300px] p-2 bg-popover border rounded-md shadow-md">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium px-2">Add Dependency</Label>
                          <p className="text-xs text-muted-foreground px-2">This task depends on:</p>
                          {availableCardsForDependency.length === 0 ? (
                            <div className="px-2 py-4 text-sm text-muted-foreground">No available tasks</div>
                          ) : (
                            <div className="max-h-[200px] overflow-y-auto">
                              {availableCardsForDependency.map((c) => (
                                <button
                                  key={c.card_id}
                                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
                                  onClick={() => addDependencyMutation.mutate(c.card_id)}
                                >
                                  <Link2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{c.card_title}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Display Section - Shows what's been added */}
                {(localMembers.length > 0 || priority || dueDate || estimatedHours || isRecurring || checklistProgress.total > 0 || localDependencies.length > 0) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Assignees */}
                    {localMembers.length > 0 && (
                      <div className="flex items-center gap-1">
                        {localMembers.map((member) => (
                          <div
                            key={member.user_id}
                            className="flex items-center gap-1 bg-muted rounded-full pl-1 pr-2 py-1 group"
                          >
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-[10px]">
                                {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs">{member.full_name}</span>
                            {mode === 'edit' && (
                              <button
                                onClick={() => removeMemberMutation.mutate(member.user_id)}
                                className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Due Date */}
                    {dueDate && (
                      <div
                        className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm cursor-pointer hover:bg-muted/80 group"
                        onClick={() => setDueDatePopoverOpen(true)}
                      >
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs">
                          {new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDueDate('')
                            if (mode === 'edit') setTimeout(handleSave, 0)
                          }}
                          className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Effort */}
                    {estimatedHours && (
                      <div
                        className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm cursor-pointer hover:bg-muted/80 group"
                        onClick={() => setEffortPopoverOpen(true)}
                      >
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs">{estimatedHours}h</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEstimatedHours('')
                            if (mode === 'edit') setTimeout(handleSave, 0)
                          }}
                          className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Priority */}
                    {priority && (
                      <div
                        className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm cursor-pointer hover:bg-muted/80 group"
                        onClick={() => setPriorityPopoverOpen(true)}
                      >
                        <Flag className={`h-3 w-3 ${
                          priority === 'URGENT' ? 'text-red-500' :
                          priority === 'HIGH' ? 'text-orange-500' :
                          priority === 'MEDIUM' ? 'text-blue-500' :
                          'text-gray-500'
                        }`} />
                        <span className="capitalize text-xs">{priority.toLowerCase()}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setPriority('')
                            if (mode === 'edit') setTimeout(handleSave, 0)
                          }}
                          className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Recurring */}
                    {isRecurring && recurrenceFrequency && (
                      <div
                        className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm cursor-pointer hover:bg-muted/80 group"
                        onClick={() => setRecurringPopoverOpen(true)}
                      >
                        <Repeat className="h-3 w-3 text-muted-foreground" />
                        <span className="capitalize text-xs">{recurrenceFrequency.toLowerCase()}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsRecurring(false)
                            setRecurrenceFrequency('')
                            setRecurrenceStartDate('')
                            setRecurrenceEndDate('')
                            if (mode === 'edit') setTimeout(handleSave, 0)
                          }}
                          className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Checklist Progress */}
                    {checklistProgress.total > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm">
                        <CheckSquare className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs">{checklistProgress.completed}/{checklistProgress.total}</span>
                      </div>
                    )}

                    {/* Dependencies */}
                    {localDependencies.length > 0 && localDependencies.map((dep) => (
                      <div
                        key={dep.dependency_id}
                        className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-sm group"
                      >
                        <Link2 className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs truncate max-w-[150px]" title={dep.depends_on_card_title}>
                          {dep.depends_on_card_title || 'Dependency'}
                        </span>
                        {mode === 'edit' && (
                          <button
                            onClick={() => removeDependencyMutation.mutate(dep.dependency_id)}
                            className="ml-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Description Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    <AlignLeft className="h-4 w-4" />
                    Description
                  </div>
                  <div className="border rounded-lg bg-slate-50 dark:bg-slate-900">
                    <RichTextEditor
                      content={description}
                      onChange={setDescription}
                      onBlur={mode === 'edit' ? handleSave : undefined}
                      placeholder="Add a more detailed description..."
                      minHeight="200px"
                    />
                  </div>
                </div>

                {/* Checklist Section - Always show */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      <CheckSquare className="h-4 w-4" />
                      Checklist
                    </div>
                    {checklistProgress.total > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {checklistProgress.completed}/{checklistProgress.total} Done
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  {checklistProgress.total > 0 && (
                    <Progress
                      value={(checklistProgress.completed / checklistProgress.total) * 100}
                      className="h-2"
                      indicatorClassName="bg-[#7BBDE8]"
                    />
                  )}

                  {/* Existing checklists for edit mode */}
                  {mode === 'edit' && localChecklists.map((checklist) => (
                    <div key={checklist.checklist_id} className="space-y-2 bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{checklist.checklist_name}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => deleteChecklistMutation.mutate(checklist.checklist_id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {checklist.items?.map((item) => (
                          <div key={item.item_id} className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-white dark:hover:bg-slate-800 group">
                            <Checkbox
                              checked={item.is_completed}
                              onCheckedChange={(checked) => updateChecklistItemMutation.mutate({
                                itemId: item.item_id, isCompleted: checked as boolean,
                              })}
                            />
                            <span className={`flex-1 text-sm ${item.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                              {item.item_text}
                            </span>
                            <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={() => deleteChecklistItemMutation.mutate(item.item_id)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2 pt-2 border-t">
                          <Input
                            value={newItemTexts[checklist.checklist_id] || ''}
                            onChange={(e) => setNewItemTexts(prev => ({ ...prev, [checklist.checklist_id]: e.target.value }))}
                            placeholder="Add an item..."
                            className="h-8 text-sm"
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddChecklistItem(checklist.checklist_id) }}
                          />
                          <Button size="sm" variant="secondary" onClick={() => handleAddChecklistItem(checklist.checklist_id)}>Add</Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pending checklists for create mode */}
                  {mode === 'create' && pendingChecklists.map((checklist) => (
                    <div key={checklist.id} className="space-y-2 bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{checklist.name}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => handleDeletePendingChecklist(checklist.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {checklist.items.map((item) => (
                          <div key={item.id} className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-white dark:hover:bg-slate-800 group">
                            <Checkbox disabled />
                            <span className="flex-1 text-sm">{item.text}</span>
                            <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={() => handleDeletePendingChecklistItem(checklist.id, item.id)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2 pt-2 border-t">
                          <Input
                            value={newItemTexts[checklist.id] || ''}
                            onChange={(e) => setNewItemTexts(prev => ({ ...prev, [checklist.id]: e.target.value }))}
                            placeholder="Add an item..."
                            className="h-8 text-sm"
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddChecklistItem(checklist.id) }}
                          />
                          <Button size="sm" variant="secondary" onClick={() => handleAddChecklistItem(checklist.id)}>Add</Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add checklist button */}
                  <Popover open={addingChecklist} onOpenChange={setAddingChecklist}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-muted-foreground">
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Add Checklist
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <Label className="text-sm">Checklist Name</Label>
                        <Input
                          value={newChecklistName}
                          onChange={(e) => setNewChecklistName(e.target.value)}
                          placeholder="Enter name..."
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAddChecklist()
                              setAddingChecklist(false)
                            }
                            if (e.key === 'Escape') setAddingChecklist(false)
                          }}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => { handleAddChecklist(); setAddingChecklist(false) }}>Add</Button>
                          <Button size="sm" variant="ghost" onClick={() => setAddingChecklist(false)}>Cancel</Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>


              </div>
            </ScrollArea>
          </div>

          {/* Right sidebar */}
          <div className="w-64 border-l bg-slate-50 dark:bg-slate-900 flex flex-col">
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-3">

                {/* Custom Fields Section */}
                {cardFieldConfigs.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Custom Fields
                    </Label>
                    <div className="space-y-2">
                      {cardFieldConfigs.map((config: FieldConfiguration) => (
                        <div key={config.config_id} className="space-y-1">
                          <Label className="text-xs text-muted-foreground">
                            {config.field_label}
                            {config.is_mandatory && <span className="text-red-500 ml-0.5">*</span>}
                          </Label>
                          {config.data_type === 'DROPDOWN' && config.dropdown_options ? (
                            <Select
                              value={customFieldValues[config.config_id] || ''}
                              onValueChange={(value) => {
                                setCustomFieldValues(prev => ({ ...prev, [config.config_id]: value }))
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {config.dropdown_options.map((option: string) => (
                                  <SelectItem key={option} value={option} className="text-xs">
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : config.data_type === 'DATE' ? (
                            <Input
                              type="date"
                              value={customFieldValues[config.config_id] || ''}
                              onChange={(e) => {
                                setCustomFieldValues(prev => ({ ...prev, [config.config_id]: e.target.value }))
                              }}
                              className="h-8 text-xs"
                            />
                          ) : config.data_type === 'NUMBER' ? (
                            <Input
                              type="number"
                              value={customFieldValues[config.config_id] || ''}
                              onChange={(e) => {
                                setCustomFieldValues(prev => ({ ...prev, [config.config_id]: e.target.value }))
                              }}
                              placeholder="Enter number..."
                              className="h-8 text-xs"
                            />
                          ) : (
                            <Input
                              value={customFieldValues[config.config_id] || ''}
                              onChange={(e) => {
                                setCustomFieldValues(prev => ({ ...prev, [config.config_id]: e.target.value }))
                              }}
                              placeholder="Enter value..."
                              className="h-8 text-xs"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <Paperclip className="h-3 w-3" />
                      Attachments {localAttachments.length > 0 && `(${localAttachments.length})`}
                    </Label>
                    {mode === 'edit' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadAttachmentMutation.isPending}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </>
                    )}
                  </div>
                  {mode === 'edit' ? (
                    localAttachments.length > 0 ? (
                      <div className="space-y-1.5 overflow-hidden">
                        {localAttachments.map((attachment) => (
                          <div
                            key={attachment.attachment_id}
                            className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border text-xs group overflow-hidden"
                          >
                            <div className="flex-shrink-0">
                              {getFileIcon(attachment.file_type || '')}
                            </div>
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <p className="truncate font-medium max-w-[120px]" title={attachment.file_name}>{attachment.file_name}</p>
                              <p className="text-muted-foreground text-[10px]">
                                {formatFileSize(attachment.file_size || 0)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <a
                                href={attachment.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 hover:bg-muted rounded"
                              >
                                <Download className="h-3 w-3" />
                              </a>
                              <button
                                onClick={() => deleteAttachmentMutation.mutate(attachment.attachment_id)}
                                className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-500"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">No attachments</p>
                    )
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2 italic">Save task to add attachments</p>
                  )}
                </div>

                {/* Comments Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    <MessageSquare className="h-3 w-3" />
                    Comments {localComments.length > 0 && `(${localComments.length})`}
                  </div>

                  {mode === 'edit' ? (
                    <>
                      {/* Add comment */}
                      <div className="space-y-1.5">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Write a comment..."
                          rows={2}
                          className="text-xs resize-none min-h-[60px]"
                        />
                        <Button
                          size="sm"
                          className="w-full h-7 text-xs bg-primary hover:bg-primary/90"
                          onClick={handleAddComment}
                          disabled={!newComment.trim()}
                        >
                          Post
                        </Button>
                      </div>

                      {/* Existing comments */}
                      {localComments.length > 0 && (
                        <div className="space-y-2 pt-2 border-t">
                          {localComments.slice(0, 5).map((comment) => {
                            const initials = comment.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'
                            return (
                              <div key={comment.comment_id} className="space-y-0.5">
                                <div className="flex items-center gap-1.5">
                                  <Avatar className="h-4 w-4">
                                    <AvatarFallback className="text-[8px] bg-primary text-primary-foreground">
                                      {initials}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-[11px] font-medium truncate">{comment.user_name?.split(' ')[0]}</span>
                                  <span className="text-[10px] text-muted-foreground ml-auto">
                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }).replace('about ', '')}
                                  </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground pl-5 bg-white dark:bg-slate-800 rounded px-2 py-1">
                                  {comment.comment_text}
                                </p>
                              </div>
                            )
                          })}
                          {localComments.length > 5 && (
                            <p className="text-[10px] text-muted-foreground text-center">
                              +{localComments.length - 5} more comments
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2 italic">Save task to add comments</p>
                  )}
                </div>

                {/* Activity Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    <History className="h-3 w-3" />
                    Activity {activityLogs.length > 0 && `(${activityLogs.length})`}
                  </div>

                  {mode === 'edit' ? (
                    activityLogs.length > 0 ? (
                      <div className="space-y-2">
                        {activityLogs.slice(0, 10).map((log) => {
                          const initials = log.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'
                          return (
                            <div key={log.log_id} className="flex items-start gap-2">
                              <Avatar className="h-5 w-5 mt-0.5">
                                <AvatarFallback className="text-[8px] bg-muted">
                                  {initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px]">
                                  <span className="font-medium">{log.user_name?.split(' ')[0] || 'Someone'}</span>
                                  {' '}
                                  <span className="text-muted-foreground">{getActivityDescription(log)}</span>
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  {formatDistanceToNow(new Date(log.created_at), { addSuffix: true }).replace('about ', '')}
                                </p>
                              </div>
                            </div>
                          )
                        })}
                        {activityLogs.length > 10 && (
                          <p className="text-[10px] text-muted-foreground text-center">
                            +{activityLogs.length - 10} more activities
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">No activity yet</p>
                    )
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2 italic">Save task to see activity</p>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-white dark:bg-slate-800">
          {mode === 'edit' && (
            <>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => archiveCardMutation.mutate()}
                disabled={archiveCardMutation.isPending}
              >
                <Archive className="h-4 w-4 mr-2" />
                {archiveCardMutation.isPending ? 'Archiving...' : 'Archive'}
              </Button>
              <Button className="bg-primary hover:bg-primary/90 px-6" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          )}
          {mode === 'create' && (
            <>
              {!selectedListId && (
                <p className="text-sm text-destructive mr-auto">
                  No list available. Please create a list first.
                </p>
              )}
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                className="bg-primary hover:bg-primary/90 px-6"
                onClick={handleCreate}
                disabled={!title.trim() || !selectedListId || createCardMutation.isPending}
              >
                {createCardMutation.isPending ? 'Creating...' : 'Create Task'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}